
import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Check, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

const Pricing = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow pt-28 pb-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-apple-text mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-apple-text-secondary max-w-3xl mx-auto">
              Find the right plan for your hiring needs
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            {/* Announcement Banner */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-12">
              <div className="flex items-start">
                <AlertTriangle className="h-6 w-6 text-yellow-600 mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-yellow-800 mb-2">Special Pricing Announcement</h3>
                  <p className="text-yellow-700 mb-2">
                    We're introducing a new payment model to ensure commitment from both parties:
                  </p>
                  <ul className="list-disc pl-5 space-y-1 text-yellow-700">
                    <li>Candidates pay ₹999 before attending an interview</li>
                    <li>Employers pay ₹999 before scheduling an interview with a candidate</li>
                  </ul>
                  <p className="mt-2 text-yellow-700">
                    This approach ensures that both parties are serious about the hiring process, reducing no-shows and improving the experience for everyone.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {/* For Candidates */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-xl font-semibold text-apple-text mb-2">For Candidates</h3>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-apple-text">₹999</span>
                    <span className="text-apple-text-secondary ml-2">one-time fee</span>
                  </div>
                  <p className="mt-4 text-apple-text-secondary">
                    Access to our platform and interview scheduling
                  </p>
                </div>
                <div className="p-6">
                  <ul className="space-y-4">
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">Complete profile creation</span>
                    </li>
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">AI-powered skill assessment</span>
                    </li>
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">Video introduction recording</span>
                    </li>
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">Application to unlimited jobs</span>
                    </li>
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">Scheduled interviews with employers</span>
                    </li>
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">Payment due before attending interviews</span>
                    </li>
                  </ul>
                  <Link 
                    to="/signup" 
                    className="mt-8 block w-full py-3 bg-apple-blue text-white text-center font-medium rounded-full hover:bg-opacity-90 transition-all"
                  >
                    Get Started
                  </Link>
                </div>
              </div>

              {/* For Employers */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 transform lg:scale-105">
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
                  <h3 className="text-xl font-semibold text-white mb-2">For Employers</h3>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-white">₹999</span>
                    <span className="text-white text-opacity-90 ml-2">per interview</span>
                  </div>
                  <p className="mt-4 text-white text-opacity-90">
                    Find the best candidates for your positions
                  </p>
                </div>
                <div className="p-6">
                  <ul className="space-y-4">
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">Unlimited job postings</span>
                    </li>
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">Access to candidate database</span>
                    </li>
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">AI-powered candidate matching</span>
                    </li>
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">View video introductions</span>
                    </li>
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">Schedule interviews with candidates</span>
                    </li>
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">Payment due before scheduling interviews</span>
                    </li>
                  </ul>
                  <Link 
                    to="/employer-login" 
                    className="mt-8 block w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-center font-medium rounded-full hover:opacity-90 transition-all"
                  >
                    Get Started
                  </Link>
                </div>
              </div>

              {/* Enterprise */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-xl font-semibold text-apple-text mb-2">Enterprise</h3>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-apple-text">Custom</span>
                  </div>
                  <p className="mt-4 text-apple-text-secondary">
                    Tailored solutions for larger organizations
                  </p>
                </div>
                <div className="p-6">
                  <ul className="space-y-4">
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">All employer features</span>
                    </li>
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">Customizable assessment process</span>
                    </li>
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">Advanced analytics dashboard</span>
                    </li>
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">Dedicated account manager</span>
                    </li>
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">API access for integration</span>
                    </li>
                    <li className="flex">
                      <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-apple-text-secondary">Volume discounts available</span>
                    </li>
                  </ul>
                  <Link 
                    to="/enterprise" 
                    className="mt-8 block w-full py-3 border border-apple-blue text-apple-blue text-center font-medium rounded-full hover:bg-apple-blue hover:text-white transition-all"
                  >
                    Contact Sales
                  </Link>
                </div>
              </div>
            </div>

            <div className="bg-apple-gray rounded-xl p-8 text-center">
              <h3 className="text-2xl font-semibold text-apple-text mb-4">Have Questions?</h3>
              <p className="text-apple-text-secondary mb-6 max-w-2xl mx-auto">
                Our team is here to help you choose the right plan for your needs. Contact us for more information.
              </p>
              <Link 
                to="/contact" 
                className="inline-block px-6 py-3 bg-apple-blue text-white font-medium rounded-full hover:bg-opacity-90 transition-all"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Pricing;
